import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Camera, Music2, Truck, Send, Loader2, ShieldCheck, Clock } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_a369e460-91b8-45cf-ab2f-6805bc15e17c/artifacts/riz8gmwr_w13dd3z5_event%20%281%29.png";

const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna", "Friuli-Venezia Giulia",
  "Lazio", "Liguria", "Lombardia", "Marche", "Molise", "Piemonte", "Puglia", "Sardegna",
  "Sicilia", "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const ARTIST_CATEGORIES = [
  { key: "fotografo", label: "Fotografo" },
  { key: "dronista", label: "Dronista" },
  { key: "mangiafuoco", label: "Mangiafuoco / Fire show" },
  { key: "giocoliere", label: "Giocoliere" },
  { key: "clown", label: "Clown / Mimo / Comicità" },
  { key: "trapezzista", label: "Acrobata / Trapezzista" },
  { key: "trampolieri", label: "Trampolieri" },
  { key: "circus", label: "Circo / Performance" },
  { key: "mago", label: "Mago / Prestigiatore" },
  { key: "band", label: "Band / Orchestra" },
  { key: "musicista", label: "Musicista" },
  { key: "street_artist", label: "Artista di strada / Busker" },
  { key: "animatore", label: "Animatore / Intrattenitore" },
  { key: "spogliarellista", label: "Burlesque" },
  { key: "dj", label: "DJ" },
];

const SUPPLIER_CATEGORIES = [
  "Beverage Horeca", "Food Service Horeca", "Cash & Carry", "Distribuzione Food",
  "Horeca Food", "Carne", "Pesce e Surgelati", "Ittico Horeca", "Salumi", "Formaggi",
  "Latticini", "Pasta", "Pomodoro", "Conserve", "Surgelati", "Caffè", "Gelateria",
  "Dessert e Bakery", "Farine e Bakery", "Gourmet Premium", "Distillati Premium",
  "Catering Food", "Beverage", "Altro",
];

const KIND_LABEL = {
  photographer: { label: "Fotografo", kind: "artist", defaultCategory: "fotografo" },
  artist: { label: "Artista", kind: "artist", defaultCategory: "dj" },
  supplier: { label: "Fornitore Food", kind: "supplier", defaultCategory: "Horeca Food" },
};

// ─────────────────────────────────────────────────────────────
// Step 1: Kind selection — choose what you are, then Google
// ─────────────────────────────────────────────────────────────
export default function RegisterPro() {
  const navigate = useNavigate();
  const continueWithGoogle = (kind) => {
    localStorage.setItem("pro_reg_kind", kind);
    const redirect = window.location.origin + "/iscriviti/scheda";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#EFF6FF]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563EB] mb-6" data-testid="btn-back-login">
          <ArrowLeft className="w-4 h-4" /> Torna al login
        </button>

        <div className="text-center mb-10">
          <img src={LOGO} alt="Event System 360" className="h-16 mx-auto mb-6" style={{ filter: "hue-rotate(50deg) saturate(1.2)" }} />
          <p className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold">Iscriviti come professionista</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] mt-3 tracking-tight">Entra nel nostro network</h1>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            La tua scheda sarà visibile ai ristoratori e organizzatori d'eventi di tutta Italia che cercano professionisti come te.
          </p>
          <p className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> 1) Scegli la categoria · 2) Accedi con Google · 3) Compila la scheda · 4) Approvazione del team
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kind-selector">
          <KindCard icon={Camera} title="Fotografo" desc="Wedding, Eventi, Reportage, Luxury" onClick={() => continueWithGoogle("photographer")} testid="select-photographer" />
          <KindCard icon={Music2} title="Artista" desc="DJ, Band, Clown, Mago, Fire show…" onClick={() => continueWithGoogle("artist")} testid="select-artist" />
          <KindCard icon={Truck} title="Fornitore Food" desc="Carne, Pesce, Beverage, Cash & Carry…" onClick={() => continueWithGoogle("supplier")} testid="select-supplier" />
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">Cliccando su una categoria sarai reindirizzato all'accesso Google. Useremo la tua email Google come email ufficiale del profilo.</p>
      </div>
    </div>
  );
}

function KindCard({ icon: Icon, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="bg-white rounded-2xl p-6 text-left hover:-translate-y-1 hover:shadow-xl transition-all border border-slate-200 hover:border-[#2563EB]/40 group">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0F2741] grid place-items-center text-white group-hover:scale-110 transition-transform">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-ui font-bold text-xl text-[#0F2741] mt-4">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{desc}</p>
      <p className="text-xs text-[#2563EB] mt-3 font-semibold inline-flex items-center gap-1">Continua con Google →</p>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 2: After Google auth — form pre-filled with email/name
// ─────────────────────────────────────────────────────────────
export function RegisterProForm() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const kindRaw = typeof window !== "undefined" ? localStorage.getItem("pro_reg_kind") : null;
  const kindCfg = KIND_LABEL[kindRaw] || KIND_LABEL.artist;

  const [form, setForm] = useState({
    name: user?.name || "",
    category: kindCfg.defaultCategory,
    specialty: "",
    phone: "",
    website: "",
    price_eur: "",
    avg_order_eur: "",
    description: "",
    city: "",
    region: "",
  });

  useEffect(() => {
    if (user?.name && !form.name) setForm((f) => ({ ...f, name: user.name }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bounce to /iscriviti if not authenticated or no kind selected
  useEffect(() => {
    if (loading) return;
    if (!user || !kindRaw) navigate("/iscriviti", { replace: true });
  }, [loading, user, kindRaw, navigate]);

  if (loading || !user || !kindRaw) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        kind: kindCfg.kind,
        name: form.name.trim(),
        category: form.category,
        specialty: form.specialty.trim(),
        email: user.email,
        phone: form.phone.trim(),
        website: form.website.trim(),
        price_eur: parseFloat(form.price_eur) || 0,
        avg_order_eur: parseFloat(form.avg_order_eur) || 0,
        description: form.description.trim(),
        city: form.city.trim(),
        region: form.region,
      };
      await api.post("/me/register", payload);
      localStorage.removeItem("pro_reg_kind");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'iscrizione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-[#EFF6FF] p-6">
        <div className="bg-white rounded-2xl p-10 max-w-lg w-full shadow-xl text-center" data-testid="register-success">
          <Clock className="w-16 h-16 text-amber-500 mx-auto" />
          <h1 className="font-display text-3xl text-[#0F2741] mt-4">Profilo in attesa di approvazione</h1>
          <p className="text-slate-600 mt-3">
            Grazie. Il tuo profilo è stato registrato con email <strong>{user.email}</strong>.
            Il nostro team lo verificherà entro pochi giorni e ti contatterà. Riceverai il <strong className="text-sky-600">badge "Verificato"</strong> una volta approvato.
          </p>
          <p className="text-xs text-slate-400 mt-4">Stato attuale: <strong className="text-amber-600">In attesa</strong></p>
          <button onClick={async () => { await logout(); }} data-testid="btn-logout-after-reg" className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#0F2741] text-white font-semibold hover:bg-[#16385C]">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </button>
        </div>
      </div>
    );
  }

  const isSupplier = kindCfg.kind === "supplier";
  const isPhotog = kindRaw === "photographer";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#EFF6FF] py-10">
      <div className="max-w-2xl mx-auto px-6">
        <button onClick={() => navigate("/iscriviti")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563EB] mb-4" data-testid="btn-back-kind">
          <ArrowLeft className="w-4 h-4" /> Cambia tipologia
        </button>

        <form onSubmit={submit} className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200 space-y-4" data-testid="registration-form">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold">Step 3 di 4 · Compila la scheda</p>
            <h2 className="font-display text-3xl text-[#0F2741] mt-2">Iscrizione: {kindCfg.label}</h2>
            <p className="text-sm text-slate-500 mt-1">Email Google verificata: <strong>{user.email}</strong></p>
          </div>

          <Field label={isSupplier ? "Ragione sociale" : "Nome / Nome d'arte"} required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-input-name" className="form-input" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Categoria" required>
              <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="reg-input-category" className="form-input bg-white">
                {(isSupplier ? SUPPLIER_CATEGORIES : ARTIST_CATEGORIES).map((c) => {
                  const key = typeof c === "string" ? c : c.key;
                  const lbl = typeof c === "string" ? c : c.label;
                  return <option key={key} value={key}>{lbl}</option>;
                })}
              </select>
            </Field>
            {!isSupplier && (
              <Field label="Specialità (es: Luxury Wedding, Fire Show)">
                <input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} data-testid="reg-input-specialty" className="form-input" />
              </Field>
            )}
          </div>

          <Field label="Telefono">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="reg-input-phone" className="form-input" />
          </Field>

          <Field label="Sito web (https://...)">
            <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="reg-input-website" placeholder="https://" className="form-input" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Città" required>
              <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="reg-input-city" className="form-input" />
            </Field>
            <Field label="Regione" required>
              <select required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="reg-input-region" className="form-input bg-white">
                <option value="">Seleziona regione</option>
                {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>

          <Field label={isSupplier ? "Spesa media per ordine (€)" : "Prezzo medio per evento (€)"}>
            {isSupplier ? (
              <input type="number" min="0" step="50" value={form.avg_order_eur} onChange={(e) => setForm({ ...form, avg_order_eur: e.target.value })} data-testid="reg-input-avg" className="form-input" />
            ) : (
              <input type="number" min="0" step="50" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} data-testid="reg-input-price" className="form-input" />
            )}
          </Field>

          <Field label="Descrizione (opzionale)">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="reg-input-description" placeholder={isPhotog ? "Stile fotografico, esperienza, attrezzatura…" : (isSupplier ? "Specialità, brand trattati, copertura geografica…" : "Repertorio, durata tipica, esperienza…")} className="form-input" />
          </Field>

          <div className="rounded-lg bg-[#EFF6FF] border border-[#2563EB]/20 p-3 text-xs text-[#1D4ED8]">
            Stato iniziale: <strong>In attesa di approvazione</strong>. Il super admin riceverà la tua iscrizione e ti contatterà via email.
            Una volta approvato, riceverai il badge <strong>"Verificato"</strong> e la tua scheda sarà visibile a tutti gli organizzatori.
          </div>

          <button type="submit" disabled={submitting} data-testid="btn-submit-registration"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-ui font-semibold transition-colors disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Invia per approvazione
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}{required && <span className="text-rose-500"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
